import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { appInsights } from '@/utils/appInsightsUtil';

type ErrorPropertyValue = string | number | boolean;

export function formatErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    return err.message;
  }

  if (err != null && typeof err === 'object') {
    const obj = err as Record<string, unknown>;

    // { errors: { field: ['msg1', ...], ... } } — validation error dict
    if (obj.errors != null && typeof obj.errors === 'object' && !Array.isArray(obj.errors)) {
      const messages = Object.values(obj.errors as Record<string, unknown>)
        .flat()
        .map(String)
        .filter(Boolean);
      if (messages.length > 0) return messages.join('\n');
    }

    // { errors: ['msg1', 'msg2'] } — errors array
    if (Array.isArray(obj.errors)) {
      const messages = obj.errors.map(String).filter(Boolean);
      if (messages.length > 0) return messages.join('\n');
    }

    // { error: '...' } — may be a JSON string wrapping an errors object
    if (typeof obj.error === 'string' && obj.error !== '') {
      try {
        const parsed: unknown = JSON.parse(obj.error);
        if (parsed != null && typeof parsed === 'object') {
          return formatErrorMessage(parsed);
        }
      } catch {
        // not JSON, fall through
      }
      return obj.error;
    }

    // { message: '...' }
    if (typeof obj.message === 'string' && obj.message !== '') {
      return obj.message;
    }
  }

  if (typeof err === 'string' && err !== '') {
    return err;
  }

  return 'An unexpected error occurred.';
}

export const getErrorDetails = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }

  // Axios-style error
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string'
  ) {
    return error.message as string;
  }

  // Something like: throw "Oops"
  if (typeof error === 'string') {
    return error;
  }

  // Unexpected object
  if (typeof error === 'object' && error !== null) {
    try {
      return JSON.stringify(error);
    } catch {
      return 'Unknown object error (could not stringify)';
    }
  }

  // Numbers, booleans, null, undefined, etc.
  return String(error);
};

export const logWarning = (message: string) => {
  appInsights.trackTrace({
    message: message,
    severityLevel: SeverityLevel.Warning,
  });

  console.warn(message);
};

function normalizeErrorProperties(
  properties?: Record<string, unknown>
): Record<string, ErrorPropertyValue> {
  if (properties == null) {
    return {};
  }

  const normalized: Record<string, ErrorPropertyValue> = {};

  for (const [key, value] of Object.entries(properties)) {
    if (value == null) {
      continue;
    }

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      normalized[key] = value;
      continue;
    }

    normalized[key] = getErrorDetails(value);
  }

  return normalized;
}

// Log error to Application Insights and console
export const logError = (
  message: string,
  error?: unknown,
  properties?: Record<string, unknown>
) => {
  const errorDetails = error != null ? `Error: ${getErrorDetails(error)}` : '';
  const fullMessage = `${message} ${errorDetails}`.trim();

  const safeSerializeError = (value: unknown): string => {
    if (value == null || typeof value !== 'object') {
      return getErrorDetails(value);
    }

    try {
      return JSON.stringify(value, Object.getOwnPropertyNames(value));
    } catch {
      return getErrorDetails(value);
    }
  };

  const exception =
    error instanceof Error ? error : new Error(error != null ? getErrorDetails(error) : message);

  appInsights.trackException({
    exception,
    properties: {
      message,
      errorDetails: error != null ? getErrorDetails(error) : '',
      originalErrorType: error == null ? 'none' : typeof error,
      originalError: safeSerializeError(error),
      ...normalizeErrorProperties(properties),
    },
  });

  console.error(fullMessage, error);
};
